import React from 'react';
import { Paper, Typography, Box, Chip } from '@mui/material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { formatDistanceToNow } from 'date-fns';

const TaskCard = ({ task, isOverlay }) => {
    // ... existing sortable hook ...
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({
        id: task.id,
        disabled: isOverlay,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 100 : 'auto',
        opacity: isDragging ? 0 : 1,
    };
    
    // Overlay style overrides
    const finalStyle = isOverlay ? {
        transform: undefined,
        zIndex: 999,
        opacity: 0.9,
        scale: '1.02',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // High elevation
    } : style;

    return (
        <Paper
            ref={isOverlay ? null : setNodeRef}
            style={finalStyle}
            {...(!isOverlay ? listeners : {})}
            {...(!isOverlay ? attributes : {})}
            elevation={isDragging || isOverlay ? 8 : 4}
            sx={{
                p: 2,
                mb: 0.5, // Reduced whitespace
                borderRadius: 4, // Match AvatarSelection
                bgcolor: task.isUnverified ? '#fff5f5' : task.isPeriodCompleted ? '#f7fafc' : 'white', // Faded red for unverified, gray for period done
                border: task.isUnverified ? '2px dashed #fc8181' : task.isPeriodCompleted ? '2px dashed #cbd5e0' : 'none',
                opacity: task.isPeriodCompleted ? 0.75 : 1,

                cursor: 'grab',
                touchAction: 'none',
                position: 'relative',
                transition: 'all 0.2s',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 6
                },
                '&:active': { cursor: 'grabbing' }
            }}
        >
            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.2rem', lineHeight: 1.2, color: '#2d3748' }}>
                {task.title}
            </Typography>
            
            {/* Show Last Done Timestamp for DB Chores */}
            {task.originalChore?.last && (
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5, fontStyle: 'italic' }}>
                    Last done: {formatDistanceToNow(task.originalChore.last.toDate ? task.originalChore.last.toDate() : new Date(task.originalChore.last))} ago
                </Typography>
            )}
            
            <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>

                {task.frequency && (
                    <Chip 
                        label={task.frequency}
                        size="small"
                        sx={{ 
                            bgcolor: task.frequency === 'daily' ? '#c6f6d5' : task.frequency === 'weekly' ? '#bee3f8' : '#e9d8fd',
                            color: task.frequency === 'daily' ? '#22543d' : task.frequency === 'weekly' ? '#2c5282' : '#553c9a',
                            fontWeight: 'bold', 
                            textTransform: 'capitalize' 
                        }}
                    />
                )}
                 {task.type.includes('fixed') && (
                    <Chip 
                        label="REQUIRED"
                        size="small"
                        sx={{ bgcolor: '#bfdbfe', color: '#1e3a8a', fontWeight: 'bold' }}
                    />
                )}

                 {task.isPeriodCompleted && !task.isUnverified && (
                    <Chip 
                        label={task.frequency === 'daily' ? "Already Done Today" : task.frequency === 'weekly' ? "Already Done This Week" : "Already Completed"}
                        size="small"
                        sx={{ bgcolor: '#edf2f7', color: '#718096', fontWeight: 'bold', border: '1px dashed #a0aec0' }}
                    />
                )}
                 {task.isUnverified && (
                    <Chip 
                        label="NOT COUNTED - REDO TO FIX"
                        size="small"
                        sx={{ bgcolor: '#fed7d7', color: '#c53030', fontWeight: 'bold', border: '1px solid #c53030' }}
                    />
                )}
            </Box>
        </Paper>
    );
};

export default TaskCard;
